import WrapInputLabel from 'common/components/wrap-input-label';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import {
  useCreatePlanMutation,
  useFindPropertiesQuery,
  useUpdateSinglePlanMutation,
} from 'redux/api/ciosUsersApi/ciosUsersApi';
import {
  saveCoverageSelection, saveUpfrontSplitAmount
} from 'redux/features/plans/plansSlice';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { extractErrorMsg } from 'utils/errors';
import {
  ApplyFormContainer,
  ApplyFormField,
  ApplyFormLayout,
} from 'common/layouts/apply-form';
import { Flex, Grid, Text } from '@chakra-ui/react';
import ContactCard from 'components/shared/contact-card';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import Input from 'common/components/input';
import FormError from 'common/components/form-error';
import {
  planStatusEnum,
  propertyStatusEnum,
  backgroundTypeEnum,
  propertyKindEnum,
} from 'common/utils/enums';
import { bedroomOptions, getFilteredBedroomOptions } from 'common/utils/selectOptions';
import {
  createPropertyOptionMenu,
  propertyFilterOption,
} from 'common/components/dropdowns';
import SelectInput from 'common/components/select-input';
import { FaHouseLaptop } from 'react-icons/fa6';
import { AddressAutofill } from '@mapbox/search-js-react';
import IconImage from 'common/components/icon-image';
import RadioCard from 'components/shared/radio-card';
import { MonthlyRentHelperLink } from 'components/plans/monthly-rent-helper-modal';
import { UnitLayoutHelperLink } from 'components/plans/unit-layout-helper-modal';

const CreateQuote = ({
  planId,
  planData,
  formikRef,
  onNextClick,
  ready,
  onBackClick,
}) => {
  const dispatch = useDispatch();
  const [wasReferred, setWasReferred] = useState(null);

  useEffect(() => {
    if (!isEmpty(planData?.property)) {
      setWasReferred(true);
    } else if (
      !isEmpty(planData?.lease?.address?.streetAddress1) &&
      isEmpty(planData?.property)
    ) {
      setWasReferred(false);
    }
  }, [planData]);
  // API mutations
  const [
    createAPlan,
    { isLoading: createPlanLoading, isSuccess: isCreatePlanSuccess },
  ] = useCreatePlanMutation();
  const [
    updatePlan,
    { isLoading: updatePlanLoading, isSuccess: isUpdatePlanSuccess },
  ] = useUpdateSinglePlanMutation();

  // SIDE EFFECTS
  // User tracking
  const {
    trackFormSubmission,
    trackFormSubmissionSuccess,
    trackFormSubmissionError,
    trackCustomEvent,
    trackPageLoad,
  } = useUserTracker();

  useEffect(() => {
    trackPageLoad('Create Quote');
  }, []);

  const {
    data: propertiesData,
    isError,
    error,
    isLoading: findPropertiesLoading,
  } = useFindPropertiesQuery({
    sort: '-createdAt',
    status: [
      propertyStatusEnum.OPTIN,
      propertyStatusEnum.OPTOUT,
      propertyStatusEnum.REQUIRED,
    ],
    leaveAnytimeEnabled: true,
  });

  // Create select options for properties
  const propertyOptions = useMemo(
    () => createPropertyOptionMenu(propertiesData),
    [propertiesData]
  );

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={{
        property: planData?.property?.id || '',
        address: !isEmpty(planData?.lease?.address)
          ? planData.lease.address
          : planData?.property?.address || {},
        monthlyRent: planData?.lease?.monthlyRent,
        numBeds: planData?.lease?.numBeds,
      }}
      validationSchema={Yup.object().shape({
        property: Yup.string(),
        address: Yup.object().shape({
          streetAddress1: Yup.string().required('Please enter your street address'),
          streetAddress2: Yup.string(),
          country: Yup.string().required('Please select your country'),
          city: Yup.string().required('Please enter your city'),
          state: Yup.string().required('Please select your state or province'),
          postalCode: Yup.string().required('Please enter your postal or zip code'),
        }),
        monthlyRent: Yup.number().required('Please enter your monthly rent amount'),
        numBeds: Yup.string()
          .required('Please select the number of bedrooms')
          .test('valid-bedroom-for-property', 'Please select a valid bedroom option for this property', function(value) {
            if (!planData?.property?.rates?.underwriting?.rentalUnitBreakdown) {
              return true; // No property selected or no rates, allow any option
            }
            if (!value) {
              return true; // Let required validation handle empty values
            }
            const breakdown = planData.property.rates.underwriting.rentalUnitBreakdown;
            const unitData = breakdown[value];
            return unitData && unitData.numberOfUnits > 0 && unitData.averageRent > 0;
          }),
      })}
      onSubmit={(values) => {
        let submissionValues = {
          status: planStatusEnum.QUOTE,
          property: values?.property,
          lease: {
            ...planData?.lease,
            monthlyRent: values?.monthlyRent,
            numBeds: values?.numBeds,
            address: values?.address,
          },
        };

        if (!values?.property) {
          delete submissionValues.property;
        }

        let mutation = createAPlan;
        if (planId) {
          submissionValues.planId = planId;
          mutation = updatePlan;
        }

        trackFormSubmission(submissionValues);

        mutation(submissionValues)
          .unwrap()
          .then((data) => {
            // Clear redux state after successful submission
            dispatch(saveCoverageSelection({}));
            dispatch(saveUpfrontSplitAmount(null));
            onNextClick(data?.id);
            trackFormSubmissionSuccess(submissionValues);
          })
          .catch((error) => {
            // Changed error message to be more generic as update is disabled
            const errMsg = extractErrorMsg(
              error,
              'We were unable to create your quote. Please reload the page and try again.'
            );
            toast.error(errMsg);
            trackFormSubmissionError(submissionValues, errMsg);
          });
      }}
    >
      {({
        handleBlur,
        handleChange,
        setFieldValue,
        values,
        isValid,
        dirty,
        handleSubmit,
        setFieldTouched,
        errors,
        touched,
        submitCount,
      }) => {
        const brand = planData?.property?.brand;
        const isDarkBg =
          brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;
        return (
          <Form className='flex flex-col w-full h-full'>
            <ApplyTitle
              title='Get a ReLease Quote'
              subtitle={planData?.lease?.signDate 
                ? 'Are you renting at a ReLease-Enabled Community?'
                : 'Are you planning on renting at a ReLease-Enabled Community?'
              }
              buttons={
                <ApplyButtons
                  loading={createPlanLoading /* || updatePlanLoading */}
                  isValid={
                    isValid &&
                    (planData?.property ||
                      dirty ||
                      planData?.lease?.address?.state) &&
                    wasReferred !== null
                  }
                  moveOn={!dirty}
                  handleSubmit={handleSubmit}
                  onNextClick={onNextClick}
                  onBackClick={onBackClick}
                  disableBack={!onBackClick}
                  nextTitle={
                    !ready ? 'Get Quote' : dirty ? 'Update Quote' : 'See Quote'
                  }
                />
              }
            />

            {/* main content */}
            <ApplyContent className='h-full justify-between'>
              <ApplyFormContainer>
                {!planData?.property && (
                  <ApplyFormLayout className=''>
                    <Grid className='grid-cols-1 md:grid-cols-2 gap-6 mb-6 col-span-2'>
                      <RadioCard
                        isSelected={wasReferred === true}
                        onClick={() => setWasReferred(true)}
                        iconComponent={
                          <IconImage
                            dark={wasReferred !== true}
                            size='3xs'
                            className='-mt-0.5'
                          />
                        }
                        label={
                          <>
                            <span className='xl:hidden'>
                              Yes, I am
                            </span>
                            <span className='hidden xl:inline'>
                              {planData?.lease?.signDate 
                                ? "Yes, I'm renting at a ReLease-Enabled Community"
                                : "Yes, I'm planning on renting at one"
                              }
                            </span>
                          </>                        }
                      />
                      <RadioCard
                        isSelected={wasReferred === false}
                        onClick={() => {
                          setWasReferred(false);
                          setFieldValue('property', '');
                        }}
                        icon={FaHouseLaptop}
                        label={planData?.lease?.signDate 
                          ? "No, I'm renting elsewhere"
                          : "No, I'm planning on renting elsewhere"
                        }
                      />
                    </Grid>
                    {wasReferred !== true && (
                      <Flex className='flex-col gap-4 col-span-2 p-6 rounded-lg border-gray-200 border bg-gray-50 -mt-6 mb-6'>
                        <Flex className='flex-col gap-2 col-span-2'>
                          <Text className='font-medium text-lg inline-flex gap-3 items-center'>
                            <IconImage size='2xs' className='-mt-1' />
                            What's a ReLease-Enabled Community?
                          </Text>
                          <Text className='text-gray-500 text-base'>
                            <span className='text-black'>ReLease-Enabled Communities are properties that we partner with to provide you a better early move out experience.</span>{' '}
                            {planData?.lease?.signDate 
                              ? 'Renters at ReLease Enabled Communities enjoy the best rates on coverage and a quicker move out from when making a claim.'
                              : 'Sign a long-term lease at a ReLease Enabled Community and enjoy the best rates on coverage and a quicker move out from when you make a claim.'
                            }
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                  </ApplyFormLayout>
                )}
              {wasReferred !== null && (
                <>
                  {wasReferred === true &&
                    (planData?.property ? (
                      <ApplyFormLayout className={planData?.property ? 'pb-6' : 'py-6'}>
                        <Flex
                          className={`flex-col gap-4 col-span-2 p-6 rounded-lg border relative overflow-clip ${
                            brand
                              ? 'border-gray-200'
                              : 'border-gray-200 bg-gray-50'
                          }`}
                          style={
                            brand?.bgColor
                              ? { backgroundColor: brand.bgColor }
                              : {}
                          }
                        >
                          <Flex className='flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
                            {brand && (
                              <img
                                src={
                                  isDarkBg && brand?.logoAlt
                                    ? brand?.logo
                                    : brand?.logo
                                }
                                alt={planData?.property?.name}
                                className='max-w-[140px] max-h-[50px] object-contain'
                              />
                            )}
                            <Flex className='flex-col gap-1'>
                              <Text
                                className={`font-medium text-lg ${
                                  isDarkBg ? 'text-white' : 'text-black'
                                }`}
                              >
                                ReLease Coverage at {planData?.property?.name}
                              </Text>
                              <Text
                                className={`text-base ${
                                  isDarkBg
                                    ? 'text-white opacity-60'
                                    : 'text-black opacity-60'
                                }`}
                              >
                                {planData?.property?.address?.streetAddress1},{' '}
                                {planData?.property?.address?.city},{' '}
                                {planData?.property?.address?.state}{' '}
                                {planData?.property?.address?.postalCode}
                              </Text>
                            </Flex>
                          </Flex>
                          {brand?.backgroundHeroImage && (
                            <img
                              src={brand?.backgroundHeroImage}
                              alt={planData?.property?.name}
                              className='absolute inset-0 opacity-15 z-0 object-cover'
                            />
                          )}
                        </Flex>
                      </ApplyFormLayout>
                    ) : (
                      <ApplyFormLayout className='py-6'>
                        <Flex className='flex-col gap-4 col-span-2 p-6 rounded-lg border-gray-200 border bg-gray-50'>
                          <Flex className='flex-col gap-2 col-span-2'>
                            <Text className='font-medium text-lg'>
                              Which community are you {planData?.lease?.signDate ? 'renting' : 'planning to rent'} at?
                            </Text>
                            <Text className='text-gray-500 text-base'>
                              Renters at ReLease-Enabled Communities enjoy the benefits of the fastest claims and the best rates on coverage.
                            </Text>
                          </Flex>
                          <ApplyFormField>
                            <WrapInputLabel>Search for Community</WrapInputLabel>
                            <SelectInput
                              isClearable={false}
                              options={propertyOptions}
                              isDisabled={
                                findPropertiesLoading || isEmpty(propertiesData)
                              }
                              filterOption={propertyFilterOption}
                              placeholder={
                                findPropertiesLoading
                                  ? 'Fetching...'
                                  : isEmpty(propertiesData)
                                  ? `No available communities`
                                  : 'Search by name or address'
                              }
                              name='property'
                              value={propertyOptions.find(
                                (selectedProperty) =>
                                  selectedProperty.value === values.property
                              )}
                              onChange={(selectedOption) => {
                                const propertyId = selectedOption?.value;
                                setFieldValue('property', propertyId);
                                if (selectedOption?.address) {
                                  setFieldValue('address', selectedOption.address);
                                }
                                // Clear bedroom selection when property changes
                                setFieldValue('numBeds', '');
                              }}
                              onBlur={handleBlur}
                            />
                            <FormError name='property' />
                          </ApplyFormField>
                        </Flex>
                      </ApplyFormLayout>
                    ))}
                  
                    {/* Address Details */}
                    <ApplyFormLayout className='py-6'>
                      <Text className='font-medium text-lg'>
                        Rental Unit Address
                      </Text>
                      {/* Street Address 1 */}
                      <ApplyFormField>
                        <WrapInputLabel isRequired>
                          Street Address 1
                        </WrapInputLabel>
                        <AddressAutofill
                          browserAutofillEnabled={false}
                          accessToken={`${process.env.REACT_APP_MAPBOX_TOKEN}`}
                        >
                          <Input
                            autoComplete='street-address'
                            placeholderText='Start typing to search for an address'
                            value={values.address.streetAddress1}
                            onChange={({ target: { value } }) => {
                              setFieldValue('address.streetAddress1', value);
                            }}
                            onBlur={handleBlur}
                            name='address.streetAddress1'
                          />
                        </AddressAutofill>
                        <FormError name='address.streetAddress1' />
                      </ApplyFormField>

                      {/* Street Address 2 */}
                      <ApplyFormField>
                        <WrapInputLabel>Street Address 2</WrapInputLabel>
                        <Input
                          autoComplete='address-line2'
                          placeholderText='Your Address'
                          value={values.address.streetAddress2}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.streetAddress2', value);
                          }}
                          onBlur={handleBlur}
                          name='address.streetAddress2'
                        />
                        <FormError name='address.streetAddress2' />
                      </ApplyFormField>

                      {/* City */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>City</WrapInputLabel>
                        <Input
                          autoComplete='address-level2'
                          placeholderText='Your City'
                          value={values.address.city}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.city', value);
                          }}
                          onBlur={handleBlur}
                          name='address.city'
                        />
                        <FormError name='address.city' />
                      </ApplyFormField>

                      {/* State */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>State</WrapInputLabel>
                        <Input
                          autoComplete='address-level1'
                          placeholderText='Your State'
                          value={values.address.state}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.state', value);
                          }}
                          onBlur={handleBlur}
                          name='address.state'
                        />
                        <FormError name='address.state' />
                      </ApplyFormField>

                      {/* Zip Code */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Zip Code</WrapInputLabel>
                        <Input
                          autoComplete='postal-code'
                          placeholderText='Your Postal Code'
                          value={values.address.postalCode}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.postalCode', value);
                          }}
                          onBlur={handleBlur}
                          name='address.postalCode'
                        />
                        <FormError name='address.postalCode' />
                      </ApplyFormField>

                      {/* Country */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>Country</WrapInputLabel>
                        <Input
                          autoComplete='country-name'
                          placeholderText='Your Country'
                          value={values.address.country}
                          onChange={({ target: { value } }) => {
                            setFieldValue('address.country', value);
                          }}
                          onBlur={handleBlur}
                          name='address.country'
                        />
                        <FormError name='address.country' />
                      </ApplyFormField>
                    </ApplyFormLayout>

                    <ApplyFormLayout className='pt-6'>
                      {/* Monthly Rent */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          {`Monthly rent (USD)`}
                        </WrapInputLabel>
                        <Input
                          prefix='$'
                          placeholderText='0.00'
                          value={values.monthlyRent}
                          onChange={(event) => {
                            handleChange(event);
                            setFieldTouched('monthlyRent');
                          }}
                          onBlur={handleBlur}
                          name='monthlyRent'
                        />
                        <Text className='ml-1 text-sm text-gray-500'>
                          Your total monthly payment. <MonthlyRentHelperLink />
                        </Text>
                        <FormError name='monthlyRent' />
                      </ApplyFormField>

                      {/* Number of Bedrooms */}
                      <ApplyFormField small>
                        <WrapInputLabel isRequired>
                          {`Unit Layout`}
                        </WrapInputLabel>
                        <SelectInput
                          options={getFilteredBedroomOptions(planData?.property, values.numBeds)}
                          placeholder={planData?.property?.kind === propertyKindEnum.STUDENT 
                            ? 'Select your unit layout (entire apartment)'
                            : 'Select the number of bedrooms in your unit'
                          }
                          value={getFilteredBedroomOptions(planData?.property, values.numBeds).find(
                            (option) => option.value === values.numBeds
                          )}
                          onChange={(selectedOption) => {
                            setFieldValue('numBeds', selectedOption.value);
                          }}
                          name='numBeds'
                          isDisabled={getFilteredBedroomOptions(planData?.property, values.numBeds).length === 0}
                        />
                                                  <Text className='ml-1 text-sm text-gray-500'>
                            {planData?.property?.kind === propertyKindEnum.STUDENT 
                              ? <>Select the unit layout for your entire apartment/unit (not just your individual bedroom) <UnitLayoutHelperLink /></>
                              : getFilteredBedroomOptions(planData?.property, values.numBeds).length > 0 
                                ? 'Available unit layouts for this property'
                                : 'No unit layouts available for this property. Please contact support.'
                            }
                          </Text>
                        <FormError name='numBeds' />
                      </ApplyFormField>
                    </ApplyFormLayout>
                </>
              )}              
              </ApplyFormContainer>
              <ContactCard
                border={false}
                className='pt-4 border-t'
                title='Need help with your quote?'
                subtitle='Contact us via email or phone to get help with your quote'
              />
            </ApplyContent>            
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateQuote;
